import _merge from 'lodash/merge';
import _get from 'lodash/get';
import { Lightning, Router } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { createItemAction } from '../cta/cta.actions';
import { getSectionCode } from '../cta/cta.utils';
import { MiniPlayer } from './miniPlayer/miniPlayer.component';
import { HIDE_SIGNAL } from './miniPlayer/miniPlayer.constants';
import SidenavItem from './sidenavItem/sidenavItem.component';
import { HEIGHT as ITEM_HEIGHT } from './sidenavItem/sidenavItem.style';
import { SidenavLogo } from './sidenavLogo/sidenavLogo.component';
import { getInitialStyle, defaultContext, colors, CONTENT_GAP, ITEM_GAP, MINI_PLAYER_MARGIN } from './sidenav.style';
import connect from './sidenav.connect';
import { actionToPath, isSameNavigation } from './sidenav.utils';
import { WIDTH, EXPANDED_WIDTH, animationConfig, DEFAULT_SERVICE } from './sidenav.constants';
import { HOME_PATH } from '../routes/routes.utils';

export default class Sidenav extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        Gradient: {},
        Items: {},
        Border: {
          rect: true,
        },
        Logo: {
          type: SidenavLogo,
        },
        MiniPlayerWrapper: {
          MiniPlayer: {
            type: MiniPlayer,
            signals: {
              [HIDE_SIGNAL]: '_hideMiniPlayer',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._isActive = false;
    this._section = null;
    this._navigation = null;
    this._isMiniPlayerFocused = false;
    this._index = 0;
    this._itemsCount = 0;
    this._currentRoute = '';
    this._currentPaths = [];
    this._currentSections = [];
    this._style = defaultContext;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _active() {
    const { screenSize } = this._style;
    const w = WIDTH[screenSize];
    this.tag('MiniPlayer').close(w - MINI_PLAYER_MARGIN[screenSize] * 2);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._isMiniPlayerFocused = false;
  }

  set isActive(value) {
    if (this._isActive !== value) {
      this.alpha = value ? 1 : 0;
      this._isActive = value;
    }
  }

  get isActive() {
    return this._isActive;
  }

  set couldRequestNavigationLayout(value) {
    this._couldRequestNavigationLayout = value;
    if (this._couldRequestNavigationLayout && this._section) {
      this.logoSection = this._section;
    }
  }

  set section(value) {
    if (this._section !== value) {
      this._navigation = null;
      this._doTheThing();

      this.logoSection = value;
      this._section = value;
    }
  }

  set logoSection(value) {
    this.tag('Logo').section = value;
  }

  get colorMode() {
    return this._style.colorMode;
  }

  set colorMode(value) {
    this._style = { ...this._style, colorMode: value };

    this.patch({
      color: colors.background[this._style.colorMode],
    });
  }

  get navigation() {
    return this._navigation;
  }

  set navigation(value) {
    if (!isSameNavigation(this._navigation, value)) {
      this._navigation = value;
      this._doTheThing();
    }
  }

  _doTheThing() {
    if (this._navigation && this._navigation.entries) {
      const { screenSize } = this._style;
      const { entries } = this._navigation;
      const itemHeight = ITEM_HEIGHT[screenSize];
      const contentGap = CONTENT_GAP[screenSize];
      const itemGap = ITEM_GAP[screenSize];

      this._itemsCount = entries.map(entry => entry.length).reduce((a, b) => a + b, 0);
      let groupCount = 0;
      let gap = 0;

      this.tag('Items').children = entries.flatMap(group => {
        this._setIndex(Math.min(Math.max(this._index, 0), this._itemsCount - 1));
        this._currentPaths = group.map(({ entry }) => actionToPath({ action: entry }));
        this._currentSections = group.map(({ entry }) => getSectionCode({ action: entry }));
        const itemOffset = itemHeight + contentGap;
        const itemsSize = itemOffset * this._itemsCount - contentGap;
        const offset = Math.round((this.finalH - itemsSize) / 2);

        const children = group.map(({ entry }, index) => ({
          type: SidenavItem,
          w: WIDTH[screenSize],
          y: offset + (index + groupCount) * itemOffset + gap,
          ref: `SidenavItem${index}`,
          item: {
            label: entry.label,
            content: entry,
          },
          actionPath: this._currentPaths[index],
          actionSection: this._currentSections[index],
          expanded: Boolean(this._isFocused),
        }));

        groupCount += group.length;
        gap += itemGap;

        return children;
      });
    } else {
      this.tag('Items').children = [];
    }

    this._updateSideNav();
  }

  set currentRoute(value) {
    this._currentRoute = value;
    this._updateSideNav();
  }

  isItemSelected(item) {
    if (item.actionSection !== this._section) {
      return false;
    }

    if (item.actionPath === this._currentRoute) {
      return true;
    }

    if (DEFAULT_SERVICE && this._currentRoute === `app/service/${DEFAULT_SERVICE}`) {
      return item.actionPath === HOME_PATH;
    }

    return false;
  }

  _updateSideNav() {
    this.tag('Items').children.forEach((item, index) => {
      const isSelected = this.isItemSelected(item);
      item.isSelected = isSelected;
      this._index = isSelected ? index : this._index;
    });
  }

  _setIndex(index = this._index) {
    this._index = index;
  }

  _focus() {
    const { screenSize } = this._style;
    const w = EXPANDED_WIDTH[screenSize];

    this.setSmooth('w', w, animationConfig);
    this._setIndex();
    this.tag('Items').children.forEach(child => {
      child.expanded = true;
    });
    this.tag('Logo').setSmooth('alpha', 1);
    this.tag('Border').setSmooth('alpha', 1, { delay: 0.15, duration: 0.2 });
    this.tag('Gradient').setSmooth('alpha', 1, animationConfig);
    this.tag('MiniPlayer').open(w - MINI_PLAYER_MARGIN[screenSize] * 2);

    this._isFocused = true;
  }

  _unfocus() {
    const { screenSize } = this._style;
    const w = WIDTH[screenSize];

    this.setSmooth('w', w, animationConfig);
    this.tag('Items').children.forEach((child, index) => {
      child.expanded = false;

      if (this.isItemSelected(child)) {
        this._index = index;
      }
    });
    this.tag('Logo').setSmooth('alpha', 0);
    this.tag('Border').setSmooth('alpha', 0, { duration: 0.2 });
    this.tag('Gradient').setSmooth('alpha', 0, animationConfig);
    this.tag('MiniPlayer').close(w - MINI_PLAYER_MARGIN[screenSize] * 2);
    this._isMiniPlayerFocused = false;

    this._isFocused = false;
  }

  _getFocused() {
    return this._isMiniPlayerFocused ? this.tag('MiniPlayer') : this.tag('Items').children[this._index];
  }

  _handleEnter() {
    if (!this._navigation || !this._navigation.entries) {
      return false;
    }

    const itemToAction = this._navigation.entries.flatMap(entry => entry)[this._index].entry;
    const itemType = _get(itemToAction, 'target.type', '');
    Store.dispatch(
      createItemAction({
        item: { action: itemToAction },
      }),
    );

    if (itemType !== 'lock') {
      this._returnToPage();
    }

    return true;
  }

  _handleUp() {
    if (this._isMiniPlayerFocused) {
      this._isMiniPlayerFocused = false;

      return true;
    }

    if (this._index > 0) {
      this._setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._index < this._itemsCount - 1) {
      this._setIndex(this._index + 1);

      return true;
    }

    if (!this._isMiniPlayerFocused && this.tag('MiniPlayer').isActive) {
      this._isMiniPlayerFocused = true;
    }

    return false;
  }

  _hideMiniPlayer() {
    this._isMiniPlayerFocused = false;
    this._refocus();
  }

  _handleRight() {
    return false;
  }

  _handleBack() {
    this._returnToPage();

    return true;
  }

  _returnToPage() {
    Router.focusPage();
  }
}
